/* eslint-disable react-hooks/exhaustive-deps */
import './styles.scss';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Avatar,
  Button,
  message,
  Typography,
} from 'antd';
import { Helmet } from 'react-helmet';
import {
  useParams,
  useSearchParams,
} from 'react-router-dom';

import UserAPI from '../../api/UserAPI';
import Header from '../../components/Header/Header';
import {
  getPosts,
  setIsUserProfilePage,
  setPostState,
  setSelected,
} from '../../context/activityReducer';
import {
  useTQConnectUserProfileInfo,
} from '../../hooks/queries/tqConnectQueries/user';
import showAppError from '../../shared/error';
import {
  useAppDispatch,
  useAppSelector,
} from '../../shared/hooks';
import { IPointsConfig } from '../../types/HostMetadata';
import { Onboarding } from '../../types/userTypes';
import RemarksTab from './components/RemarksTab/RemarksTab';
import ReviewsTab from './components/ReviewsTab/ReviewsTab';
import ShoutoutsTab from './components/ShoutoutsTab/ShoutoutsTab';
import UserProfileAbout from './components/UserProfileAbout';
import UserProfileCard from './components/UserProfileCard';
import UserProfilePosts from './components/UserProfilePosts';
import UserProfileSkeleton from './components/UserProfileSkeleton';
import {
  IUserProfileDetails,
  UserAccountType,
  UserProfileTabs,
  UserProfileView,
  UserProfileViewType,
} from './types';

interface Props {
  handleBack?: () => void;
}

const UserProfile: React.FC<Props> = ({ handleBack }) => {
  // const navigate = useAppNavigate();

  const { userId } = useParams();

  const [searchParams] = useSearchParams();

  const tab = searchParams.get('tab');

  const dispatch = useAppDispatch();

  const { isTagMango, isLoggedIn, hostMetadata } = useAppSelector(
    (state) => state.app,
  );
  const { selected, isUserProfilePage } = useAppSelector(
    (state) => state.activity,
  );
  const {
    id,
    type: userType,
    tqConnectScore,
  } = useAppSelector((state) => state.user);
  const {
    userCommunityAccess: {
      hasCommunityCreatorAccess,
      hasTQConnectAccessForUser,
    },
  } = useAppSelector((state) => state.community);

  const { pointsConfig, offeringTitles: mangoesTitle, appName } = hostMetadata;

  const [profileDetails, setProfileDetails] =
    useState<IUserProfileDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showProfileInHeader, setShowProfileInHeader] = useState(false);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const profileCardRef = useRef<HTMLDivElement>(null);

  const content = useMemo(
    () => ({
      title: `${profileDetails?.userProfile?.name || appName} - Profile`,
      metaTitle: `${profileDetails?.userProfile?.name || appName} - Profile`,
      metaDescription: `${profileDetails?.userProfile?.name || appName} - Profile`,
    }),
    [profileDetails?.userProfile?.name, appName],
  );

  const { data: tqProfileInfo, isLoading: isTQProfileInfoLoading } =
    useTQConnectUserProfileInfo({
      userId: userId as string,
      userType: profileDetails?.userProfile?.onboarding,
    });

  const checkTQScore = (score?: number) => {
    return typeof score === 'number';
  };

  const { refetch: refreshTQProfileInfo } = useTQConnectUserProfileInfo({
    userId: userId as string,
    userType: profileDetails?.userProfile?.onboarding,
  });

  const accessState = useMemo(() => {
    // the account type of the user profile
    const accountType: UserAccountType =
      profileDetails?.accountType || 'subscriber';
    // the account type of the viewer (logged in user)
    const viewerType: UserAccountType =
      profileDetails?.viewerType ||
      (userType &&
      [Onboarding.CREATOR_COMPLETED, Onboarding.CREATOR_RESTRICTED].includes(
        userType as Onboarding,
      )
        ? 'creator'
        : 'subscriber');
    // if tq connect is enabled for the user profile
    // tq connect is enabled for the user profile:
    // - community feature is active
    // - tq connect feature is active
    // - tq connect config is enabled
    // - user is a creator or has a valid tq connect score
    const tqConnectEnabledForUserProfile =
      hostMetadata?.isCommunityFeatureActive &&
      hostMetadata?.isTQConnectFeatureActive &&
      hostMetadata?.tqConnectConfig?.enabled &&
      (accountType === 'creator' ||
        (checkTQScore(tqProfileInfo?.tqConnectScore) &&
          tqProfileInfo?.hasAccess));
    // if tq connect is enabled for the viewer
    // tq connect is enabled for the viewer:
    // - community feature is active
    // - tq connect feature is active
    // - user has community creator access or tq connect access as customer and has a valid tq connect score
    const tqConnectEnabledForViewer =
      hostMetadata?.isCommunityFeatureActive &&
      hostMetadata?.isTQConnectFeatureActive &&
      (hasCommunityCreatorAccess ||
        (hasTQConnectAccessForUser && checkTQScore(tqConnectScore)));

    return {
      accountType,
      viewerType,
      tqConnectEnabledForUserProfile,
      tqConnectEnabledForViewer,
      // decide user profile view based on tq connect enabled or not for user profile and viewer
      view: (tqConnectEnabledForUserProfile && tqConnectEnabledForViewer
        ? UserProfileView.TQ_CONNECT_PROFILE
        : UserProfileView.DEFAULT_PROFILE) as UserProfileViewType,
    };
  }, [
    profileDetails?.accountType,
    profileDetails?.viewerType,
    userType,
    tqConnectScore,
    hostMetadata?.isCommunityFeatureActive,
    hostMetadata?.isTQConnectFeatureActive,
    hostMetadata?.tqConnectConfig?.enabled,
    tqProfileInfo?.hasAccess,
    tqProfileInfo?.tqConnectScore,
    hasCommunityCreatorAccess,
    hasTQConnectAccessForUser,
  ]);

  const tabsToShow = useMemo(() => {
    // default tabs to show
    let list: UserProfileTabs[] = [
      UserProfileTabs.ABOUT,
      UserProfileTabs.POSTS,
    ];

    // if it's a creator profile, show posts tab only
    if (accessState.accountType === 'creator') {
      return list;
    }

    if (
      accessState.tqConnectEnabledForViewer &&
      accessState.tqConnectEnabledForUserProfile
    ) {
      // if tq connect is enabled for viewer and user profile, add about, reviews and shoutouts tabs
      list = [
        UserProfileTabs.ABOUT,
        UserProfileTabs.POSTS,
        UserProfileTabs.REVIEWS,
        UserProfileTabs.SHOUTOUTS,
      ];
    }

    // if viewer is creator and account type is subscriber, add purchases and remarks tabs
    // if viewer is subscriber, show default tabs
    return accessState.viewerType === 'creator' &&
      accessState.accountType === 'subscriber'
      ? [...list, UserProfileTabs.REMARKS]
      : // ? [...list, UserProfileTabs.PURCHASES, UserProfileTabs.REMARKS]
        list;
  }, [
    accessState.tqConnectEnabledForViewer,
    accessState.tqConnectEnabledForUserProfile,
    accessState.viewerType,
    accessState.accountType,
  ]);

  const [activeTab, setActiveTab] = useState<UserProfileTabs>(
    (tab || UserProfileTabs.POSTS) as UserProfileTabs,
  );

  const onSelectTab = (tabItem: UserProfileTabs) => {
    setActiveTab(tabItem);
    // navigate(ROUTES.USER_PROFILE, {
    //   userId: userId as string,
    //   search: `tab=${tabItem}`,
    // });
  };

  const fetchProfileDetails = async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const response = await UserAPI.getProfileDetails(userId);
      setProfileDetails(response.data.result);
    } catch (error) {
      showAppError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const reloadUserPosts = () => {
    const newState = {
      posts: [],
      hasMore: true,
      page: 1,
      loading: false,
      refreshing: false,
    };

    dispatch(
      setPostState({
        posts: [],
        hasMore: true,
        page: 1,
      }),
    );

    if (selected === userId) {
      dispatch(
        getPosts({
          pageCount: 1,
          postList: [],
          hasMore: true,
          loading: false,
          currState: newState,
          showUserPosts: true,
        }),
      );
    }
  };

  useEffect(() => {
    if (isLoggedIn && id && userId) {
      dispatch(setIsUserProfilePage(true));
      dispatch(setSelected(userId));
      fetchProfileDetails();
    } else {
      message.error('Please login to view profile');
    }
  }, [id, isLoggedIn, userId]);

  // On refresh, set userId as selected
  useEffect(() => {
    if (isUserProfilePage && userId && selected !== userId) {
      dispatch(setSelected(userId));
    }
  }, [isUserProfilePage, userId, selected]);

  useEffect(() => {
    if (selected === userId) {
      reloadUserPosts();
    }
  }, [selected, userId]);

  // Update header when user-profile-card scrolls up (out of view)
  useEffect(() => {
    const scrollRoot = scrollContentRef.current;
    const cardEl = profileCardRef.current;
    if (!scrollRoot || !cardEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowProfileInHeader(!entry.isIntersecting);
      },
      {
        root: scrollRoot,
        rootMargin: '0px',
        threshold: 0,
      },
    );
    observer.observe(cardEl);
    return () => observer.disconnect();
  }, [isLoading, profileDetails]);

  const tabSelectorItem = (
    value: UserProfileTabs,
    name: string,
    style?: React.CSSProperties,
  ): JSX.Element => {
    const isActiveTab = value === activeTab;
    return (
      <Button
        key={value}
        className={`siteBtn tab__selector__item ${isActiveTab ? 'selected' : ''}`}
        onClick={() => onSelectTab(value)}
        style={{
          ...style,
        }}>
        <Typography.Text className="tab__selector__item__text">
          {name}
        </Typography.Text>
      </Button>
    );
  };

  const tabContent = (key: UserProfileTabs) => {
    if (!profileDetails) return null;

    switch (key) {
      case UserProfileTabs.ABOUT:
        return (
          <UserProfileAbout
            profileDetails={profileDetails as IUserProfileDetails}
            setProfileDetails={setProfileDetails}
            pointsConfig={pointsConfig as IPointsConfig}
            isTagMango={isTagMango}
            mangoesTitle={mangoesTitle}
            userId={userId as string}
            accountType={profileDetails.accountType}
            viewerType={profileDetails.viewerType}
            isViewerSelf={profileDetails.isViewerSelf}
            view={accessState.view}
          />
        );
      case UserProfileTabs.POSTS:
        return (
          <UserProfilePosts
            userId={userId as string}
            isUserProfilePage={isUserProfilePage}
            profileDetails={profileDetails as IUserProfileDetails}
          />
        );
      case UserProfileTabs.REVIEWS:
        return (
          <ReviewsTab
            userProfileId={userId as string}
            userProfileDetails={profileDetails as IUserProfileDetails}
            accountType={profileDetails?.accountType}
            viewerType={profileDetails?.viewerType}
            isViewerSelf={profileDetails?.isViewerSelf}
            reviewReceived={tqProfileInfo?.tqReviewReceived || 0}
            reviewGiven={tqProfileInfo?.tqReviewGiven || 0}
          />
        );
      case UserProfileTabs.SHOUTOUTS:
        return (
          <ShoutoutsTab
            userProfileId={userId as string}
            userProfileDetails={profileDetails}
            accountType={profileDetails?.accountType}
            viewerType={profileDetails?.viewerType}
            isViewerSelf={profileDetails?.isViewerSelf}
            shoutoutReceived={tqProfileInfo?.tqShoutoutReceived || 0}
            shoutoutGiven={tqProfileInfo?.tqShoutoutGiven || 0}
            refreshTQProfileInfo={refreshTQProfileInfo}
          />
        );
      // case UserProfileTabs.PURCHASES:
      //   return <></>;
      case UserProfileTabs.REMARKS:
        return <RemarksTab />;

      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>{content.title}</title>
        <meta name="title" content={content.metaTitle} />
        <meta name="description" content={content.metaDescription} />
        <meta
          name="keywords"
          content="tagmango, creator, artist, influencer, educator, trainer, public speaking, learn online, make money online, youtube, Instagram creator, youtube creator"
        />
      </Helmet>

      <div className="home__container user-profile__container">
        <Header
          title="Profile"
          handleBack={handleBack}
          customTitle={
            profileDetails ? (
              <div className="user-profile-header-title">
                <div
                  className={`user-profile-header-title__item ${
                    !showProfileInHeader ? 'visible' : 'hidden'
                  }`}>
                  <span className="default-header__title__text">Profile</span>
                </div>
                <div
                  className={`user-profile-header-title__item user-profile-header-title__sticky ${
                    showProfileInHeader ? 'visible' : 'hidden'
                  }`}>
                  <Avatar
                    src={profileDetails.userProfile.profilePicUrl}
                    size={28}
                    className="user-profile-header-title__avatar"
                  />
                  <span className="default-header__title__text">
                    {profileDetails.userProfile.name}
                  </span>
                </div>
              </div>
            ) : undefined
          }
        />

        <div ref={scrollContentRef} className="user-profile__content">
          <div ref={profileCardRef} className="user-profile-card">
            {!isLoading && profileDetails ? (
              <UserProfileCard
                profileDetails={profileDetails as IUserProfileDetails}
                setProfileDetails={setProfileDetails}
                pointsConfig={pointsConfig as IPointsConfig}
                isTagMango={isTagMango}
                mangoesTitle={mangoesTitle}
                userId={userId as string}
                showAboutContent={false}
                view={accessState.view}
                canRequestForService={
                  accessState.view === 'tq-connect-profile' &&
                  accessState.accountType === 'subscriber' &&
                  accessState.viewerType === 'subscriber' &&
                  !profileDetails?.isViewerSelf
                }
              />
            ) : (
              <UserProfileSkeleton />
            )}
          </div>

          {
            // !isTagMango &&
            !isLoading &&
            profileDetails &&
            selected === userId &&
            !isTQProfileInfoLoading ? (
              <div className="user-profile__content__tabs">
                <div className="tab__selector" style={{ border: 'none' }}>
                  {tabsToShow.map((tabItem) =>
                    tabSelectorItem(tabItem, tabItem.toTitleCase()),
                  )}
                </div>

                <div className="tabs__content">
                  {tabContent(activeTab as UserProfileTabs)}
                </div>
              </div>
            ) : null
          }

          {/* {
            // !isTagMango &&
            !isLoading &&
            profileDetails &&
            selected === userId &&
            !isTQProfileInfoLoading ? (
              <Tabs
                defaultActiveKey={UserProfileTabs.POSTS}
                activeKey={activeTab}
                onChange={(tabItem) => onSelectTab(tabItem as UserProfileTabs)}
                className="page__content"
                // destroyInactiveTabPane
              >
                {tabsToShow.map((tabItem) => (
                  <Tabs.TabPane key={tabItem} tab={tabItem.toTitleCase()}>
                    {tabContent(tabItem)}
                  </Tabs.TabPane>
                ))}
              </Tabs>
            ) : null
          } */}
        </div>
      </div>
    </>
  );
};

export default UserProfile;
